import { Controller } from '@hotwired/stimulus'

/**
 * Client-side table sorting with URL sync.
 *
 * Usage:
 *   <div data-controller="table--sort"
 *        data-table--sort-default-column-value="name"
 *        data-table--sort-default-direction-value="asc">
 *     <table>
 *       <thead>
 *         <tr>
 *           <th>
 *             <button data-action="click->table--sort#sort"
 *                     data-table--sort-column-param="name">
 *               Name
 *             </button>
 *           </th>
 *         </tr>
 *       </thead>
 *       <tbody data-table--sort-target="body">
 *         <tr>
 *           <td data-column="name" data-sort-value="smith">John Smith</td>
 *         </tr>
 *       </tbody>
 *     </table>
 *   </div>
 *
 * Sort values are read from data-sort-value attribute if present,
 * otherwise from textContent. Type (string/number) is inferred automatically.
 */
export default class extends Controller {
  static targets = ['body', 'sortButton', 'sortIcon']
  static values = {
    column: { type: String, default: '' },
    direction: { type: String, default: 'asc' },
    defaultColumn: { type: String, default: '' },
    defaultDirection: { type: String, default: 'asc' }
  }

  connect() {
    this.restoreFromUrl()

    // Apply default sort if no URL params and default is set
    if (!this.columnValue && this.defaultColumnValue) {
      this.columnValue = this.defaultColumnValue
      this.directionValue = this.defaultDirectionValue
    }

    if (this.columnValue) {
      this.sortRows()
      this.updateIndicators()
    }
  }

  sort(event) {
    const column = event.params.column

    // Toggle direction if same column, else default to desc
    if (this.columnValue === column) {
      this.directionValue = this.directionValue === 'desc' ? 'asc' : 'desc'
    } else {
      this.columnValue = column
      this.directionValue = 'desc'
    }

    this.sortRows()
    this.updateIndicators()
    this.syncToUrl()
  }

  sortRows() {
    if (!this.hasBodyTarget) return

    const rows = [...this.bodyTarget.querySelectorAll(':scope > tr')]
    if (rows.length === 0) return

    const col = this.columnValue
    const dir = this.directionValue

    // Infer type from first non-empty value
    const type = this.inferType(rows, col)

    rows.sort((a, b) => {
      const aVal = this.getSortValue(a, col)
      const bVal = this.getSortValue(b, col)

      return this.compare(aVal, bVal, type, dir)
    })

    rows.forEach(row => this.bodyTarget.appendChild(row))
  }

  getSortValue(row, column) {
    const cell = row.querySelector(`[data-column="${column}"]`)
    if (!cell) return ''

    // Prefer explicit sort value, fall back to text content
    return cell.dataset.sortValue ?? cell.textContent?.trim() ?? ''
  }

  inferType(rows, column) {
    // Check first few non-empty values to determine type
    for (const row of rows.slice(0, 5)) {
      const val = this.getSortValue(row, column)
      if (val === '') continue

      // If it looks like a number, treat as number
      if (/^-?\d*\.?\d+$/.test(val)) {
        return 'number'
      }

      // Otherwise string
      return 'string'
    }

    return 'string'
  }

  compare(a, b, type, direction) {
    let result

    // Handle empty values - sort them last
    if (a === '' && b === '') return 0
    if (a === '') return 1
    if (b === '') return -1

    if (type === 'number') {
      result = (parseFloat(a) || 0) - (parseFloat(b) || 0)
    } else {
      result = a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    }

    return direction === 'asc' ? result : -result
  }

  updateIndicators() {
    // Update sort buttons
    this.sortButtonTargets.forEach(button => {
      const isActive = button.dataset.column === this.columnValue
      button.classList.toggle('bg-inverse', isActive)
      button.classList.toggle('hover:bg-inverse', isActive)
      button.classList.toggle('bg-transparent', !isActive)
      button.classList.toggle('hover:bg-surface-strong', !isActive)
    })

    // Update sort icons
    this.sortIconTargets.forEach(icon => {
      const isActive = icon.dataset.column === this.columnValue
      // Inactive buttons show descending (suggesting "click to sort desc")
      // Active buttons show the current direction
      const direction = isActive ? this.directionValue : 'desc'

      icon.classList.toggle('text-content-inverse', isActive)
      icon.classList.toggle('text-content-primary', !isActive)
      icon.classList.toggle('rotate-180', direction === 'desc')
    })

    // Update aria-sort on headers
    this.element.querySelectorAll('th[data-column]').forEach(th => {
      if (th.dataset.column === this.columnValue) {
        th.setAttribute('aria-sort', this.directionValue === 'asc' ? 'ascending' : 'descending')
      } else {
        th.removeAttribute('aria-sort')
      }
    })
  }

  syncToUrl() {
    const url = new URL(window.location)
    url.searchParams.set('sort', this.columnValue)
    url.searchParams.set('direction', this.directionValue)
    history.replaceState({}, '', url)
  }

  restoreFromUrl() {
    const url = new URL(window.location)
    const sort = url.searchParams.get('sort')
    const direction = url.searchParams.get('direction')

    // Only restore if the sort column exists in this table
    if (sort && this.columnExistsInTable(sort)) {
      this.columnValue = sort
      // Validate direction - only accept 'desc', otherwise default to 'asc'
      this.directionValue = direction === 'desc' ? 'desc' : 'asc'
    }
  }

  columnExistsInTable(column) {
    // Check if the column exists in this table's headers
    return this.element.querySelector(`th[data-column="${column}"]`) !== null
  }

  disconnect() {
    // Clear sort params from URL when this table disconnects
    const url = new URL(window.location)
    if (url.searchParams.has('sort') || url.searchParams.has('direction')) {
      url.searchParams.delete('sort')
      url.searchParams.delete('direction')
      history.replaceState({}, '', url)
    }
  }
}
